import React, { useState } from "react";
import { StatCard, MetaCell, Expandable, DataTable, SectionTitle, LastSynced, PermissionBadge, EmptyState } from "./DashboardPrimitives";

const AwsDashboard = ({ data, formatLastConnected }) => {
  const [open, setOpen] = useState({ iam: true, s3: false, ec2: false, vpc: false });
  const toggle = (key) => setOpen((p) => ({ ...p, [key]: !p[key] }));

  if (!data?.connector_data) return <EmptyState message="No AWS data available" />;

  const { identity, permissions, resources } = data.connector_data;
  const ec2Instances = resources?.EC2?.Reservations?.flatMap((r) => r.Instances) || [];

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-gray-100">
        <div>
          <p className="text-sm font-semibold text-gray-800">Account ID: <span className="font-normal text-gray-500">{identity.Account}</span></p>
          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-lg">ARN: {identity.Arn}</p>
          <LastSynced value={data?.last_synced_at} formatFn={formatLastConnected} />
        </div>
      </div>

      {/* Summary */}
      <div>
        <SectionTitle title="Asset Summary" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="EC2 Instances" value={ec2Instances.length} />
          <StatCard label="S3 Buckets" value={resources?.S3?.length || 0} />
          <StatCard label="IAM Users" value={resources?.IAM_Users?.Users?.length || 0} />
          <StatCard label="VPCs" value={resources?.VPCs?.Vpcs?.length || 0} />
        </div>
      </div>

      {/* Permissions */}
      <div>
        <SectionTitle title="Permissions Visibility" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(permissions).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-white">
              <span className="text-sm text-gray-700 font-medium">{key}</span>
              <PermissionBadge ok={val} />
            </div>
          ))}
        </div>
      </div>

      {/* Detail sections */}
      <div className="space-y-3">
        <Expandable title="IAM Users" isOpen={open.iam} onToggle={() => toggle("iam")}>
          <DataTable
            headers={["User Name", "Created At", "Last Used"]}
            rows={(resources?.IAM_Users?.Users || []).map((u) => [
              u.UserName,
              new Date(u.CreateDate).toLocaleDateString(),
              u.PasswordLastUsed ? new Date(u.PasswordLastUsed).toLocaleDateString() : "—",
            ])}
          />
        </Expandable>

        <Expandable title="S3 Buckets" isOpen={open.s3} onToggle={() => toggle("s3")}>
          <DataTable
            headers={["Bucket Name", "Region", "Created At"]}
            rows={(resources?.S3 || []).map((b) => [b.BucketName, b.Region, new Date(b.CreatedAt).toLocaleDateString()])}
          />
        </Expandable>

        <Expandable title="EC2 Instances" isOpen={open.ec2} onToggle={() => toggle("ec2")}>
          <DataTable
            headers={["Instance ID", "Name", "Type", "State", "Public IP"]}
            rows={ec2Instances.map((i) => [
              i.InstanceId,
              i.Tags?.find((t) => t.Key === "Name")?.Value || "—",
              i.InstanceType,
              i.State?.Name,
              i.PublicIpAddress || "—",
            ])}
          />
        </Expandable>

        <Expandable title="VPCs" isOpen={open.vpc} onToggle={() => toggle("vpc")}>
          <DataTable
            headers={["VPC ID", "CIDR", "Default", "State"]}
            rows={(resources?.VPCs?.Vpcs || []).map((v) => [v.VpcId, v.CidrBlock, v.IsDefault ? "Yes" : "No", v.State])}
          />
        </Expandable>
      </div>
    </div>
  );
};

export default AwsDashboard;
